import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import CloseIcon from '~Assets/icons/close.svg';
import Icon from '~ComponentsImproved/Icon';
import { ORDER_STATUS } from '~../__queries__';
import { LANGUAGES, LocalItem, LocalVariation } from '~Types';
import {
  CompleteBongQuery_venue_company_layouts as Layout,
  CompleteBongQuery_venue_company_categories_items as Item,
  CompleteBongQuery_venue_company_layouts_categories_page as CategoryPage,
} from '~Scenes/POS/Queries/__queries__';
import { useEntryFinder } from '~Components/EntryFinder';
import { useButton, useI18n, useWindowSize } from '~Hooks';
import { Box, Input, SearchInputWrapper, TransparentButton } from '~Styles';
import Fuse from 'fuse.js';
import ItemButton from '../GridButtons/ItemButton';
import { useSwipeable } from 'react-swipeable';
import { ItemCategoryMap } from '~Scenes/POS/categories';

const ModalContent = styled.div`
  width: 100%;
  position: absolute;
  box-sizing: border-box;
  padding: 32px 326px 0px 64px;
`;

const ResultItems = styled.div`
  margin: 0px 40px 32px;
  width: auto;
  height: 60vh;
  overflow-y: scroll;
  background-color: #2f353d;
  border-radius: 0 0 4px 4px;
  display: flex;
  flex-flow: wrap;
  align-content: flex-start;
  padding: 8px;
`;

const InputWrapper = styled(Box)`
  position: relative;
  width: auto;
  margin: 0px 40px 0px 40px;
  border-radius: 4px;
  text-align: center;
  border-radius: 4px 4px 0 0;
  background-color: var(--colorBlueGray1200);
  border-bottom: 1px solid var(--colorBlueGray1000);
`;

const CloseButton = styled(TransparentButton)`
  border-radius: 100%;
  height: 48px;
  width: 48px;
  min-width: 48px;
  min-height: 48px;
  padding: 0;
  flex-shrink: 0;
  border: 2px solid var(--colorBlueGray600);
  margin: 20px auto 20px auto;
  background-color: var(--colorBlueGray1200);
`;

interface Props {
  toggleModal: () => void;
  layout: Layout | null | undefined;
  venueId: string;
  items: LocalItem[];
  alreadyInCartCountMap?: {
    [itemId: string]: number;
  };
  allItems: Item[];
  mapItem: (item: any) => void;
  addItem: (
    id: string,
    title: string,
    categoryId: any,
    options: {
      variations: LocalVariation[];
      removedVariations: LocalVariation[];
      message?: string | null;
      type?: string;
      count?: number;
      courseType?: any;
      isMessage?: boolean;
      parentId?: string;
      defaultOrderStatus?: ORDER_STATUS | null;
    },
    price?: number,
  ) => { cartItemIds: string[] };
  itemToCategoryMap: ItemCategoryMap;
}

const titleKeys = LANGUAGES.map((lang) => `titleLang.${lang}`);

const fuse = new Fuse<Item>([], {
  keys: titleKeys,
  includeScore: true,
  // Threshold needs to be set to 0 to get the results we are after.
  threshold: 0,
  // Ignore location helps us search through the whole item name for a match.
  ignoreLocation: true,
});

const SearchArticles: FC<Props> = ({
  layout,
  venueId,
  items,
  alreadyInCartCountMap,
  allItems,
  mapItem,
  addItem,
  toggleModal,
  itemToCategoryMap,
}) => {
  const { i18n } = useI18n();
  const [searchResults, setSearchResults] = useState<any>([]);
  const [inputValue, setInputValue] = useState('');
  const [isScrolling, setIsScrolling] = useState(false);
  const findEntryFromItem = useEntryFinder();

  const closeButton = useButton({
    onClick: () => {
      toggleModal();
      setInputValue('');
    },
  });
  const colNr = 3;
  const { height: windowHeight } = useWindowSize();
  const isScrollable = searchResults.length > (windowHeight < 1024 ? 12 : 15);

  useEffect(() => {
    fuse.setCollection(allItems);
  }, [allItems]);

  // Search for results with 300ms debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue.length > 0) {
        const results = fuse
          .search(inputValue)
          .map((result) => result.item)
          .filter((item) => findEntryFromItem(item.id))
          .map(mapItem);

        setSearchResults(results);
      } else {
        setSearchResults([]);
      }
    }, 300);

    // Clean up timeout on unmount or when inputValue changes
    return () => {
      clearTimeout(timeoutId);
    };
  }, [inputValue]);

  const renderItemButton = (button: any, index: number) => {
    const alreadyInCartCount = alreadyInCartCountMap
      ? alreadyInCartCountMap[button.itemId] ?? null
      : null; // TSR function

    return (
      <ItemButton
        key={`${button.id}-${index}`}
        buttonWidth={100 / colNr}
        venueId={venueId}
        onAddItem={addItem}
        isScrolling={isScrolling}
        cartItems={items}
        nrInCart={
          items.filter((item) => item.itemId === button?.itemId)?.length +
          (alreadyInCartCount ?? 0)
        }
        itemToCategoryMap={itemToCategoryMap}
        isLayout={!!layout}
        count={
          items.filter((item) => item.itemId === button?.itemId)?.length +
          (alreadyInCartCount ?? 0)
        }
        {...button}
      />
    );
  };

  const handlers = useSwipeable({
    delta: 10,

    onSwiping: ({ dir }) => {
      // Don't handle scroll if not necessary (unscrollable or side swipes)
      if (!isScrollable || dir === 'Left' || dir === 'Right') {
        return;
      }

      setIsScrolling(true);
    },
  });

  let timeout: number;
  const onTouchEnd = () => {
    timeout = setTimeout(() => {
      setIsScrolling(false);
    }, 100);
  };

  return (
    <ModalContent>
      <CloseButton display="flex" alignItems="center" justifyContent="center" {...closeButton}>
        <Icon src={CloseIcon} color="var(--colorBlueGray600)" width="24px" height="24px" />
      </CloseButton>

      <InputWrapper display="flex" padding="medium">
        <SearchInputWrapper fluid value={inputValue} onClear={() => setInputValue('')}>
          <Input
            id="search-articles"
            name="search-articles"
            placeholder={`${i18n('POS.Search')}...`}
            value={inputValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
            dark
            padding="medium"
            isSearch
            autoFocus
            fluid
          />
        </SearchInputWrapper>
      </InputWrapper>

      <ResultItems {...handlers} onTouchEnd={onTouchEnd}>
        {searchResults.map(renderItemButton)}
      </ResultItems>
    </ModalContent>
  );
};

export default SearchArticles;
